import React from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function Pagination({ page, pageCount, total, pageSize, onChange, testid = "pagination" }) {
  if (!total || total <= pageSize) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="pager" data-testid={testid}>
      <span className="pager-info">
        Showing <b>{start}</b>–<b>{end}</b> of <b>{total}</b>
      </span>
      <div className="pager-controls">
        <button
          type="button"
          className="pager-btn"
          data-testid={`${testid}-prev`}
          disabled={page <= 1}
          onClick={() => onChange(Math.max(1, page - 1))}
        >
          <ChevronLeft size={15} /> Prev
        </button>
        <span className="pager-page" data-testid={`${testid}-label`}>
          Page {page} / {pageCount}
        </span>
        <button
          type="button"
          className="pager-btn"
          data-testid={`${testid}-next`}
          disabled={page >= pageCount}
          onClick={() => onChange(Math.min(pageCount, page + 1))}
        >
          Next <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
}


export function Modal({ title, eyebrow = "Form operasional", onClose, children, maxWidth }) {
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="modal-backdrop"
      data-testid="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" data-testid="modal-panel" style={maxWidth ? { maxWidth, width: "100%" } : undefined}>
        <div className="modal-head">
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h2 data-testid="modal-title">{title}</h2>
          </div>
          <button
            data-testid="modal-close-button"
            className="icon-button"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Badge({ children, tone = "neutral" }) {
  return (
    <span
      data-testid={`badge-${String(children).toLowerCase().replaceAll(" ", "-")}`}
      className={`badge badge-${tone}`}
    >
      {children}
    </span>
  );
}

export function Field({ label, testid, value, onChange, type = "text", placeholder, required, disabled, step, min, max }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        step={step ?? (type === "number" ? "0.01" : undefined)}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="field">
      <span>{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) =>
          typeof o === "string" ? (
            <option key={o} value={o}>
              {o}
            </option>
          ) : (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          )
        )}
      </select>
    </label>
  );
}

export function PageIntro({ eyebrow, title, subtitle, action, testid }) {
  return (
    <div className="page-intro">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1 data-testid={testid}>{title}</h1>
        <p className="subtitle">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

export function PanelHead({ title, detail, action, onAction }) {
  return (
    <div className="panel-head">
      <div>
        <h2>{title}</h2>
        <p>{detail}</p>
      </div>
      {action && (
        <button
          data-testid="panel-action-button"
          className="text-button"
          onClick={onAction}
        >
          {action} ›
        </button>
      )}
    </div>
  );
}

export function EmptyState({ title, description, actionLabel, onAction, testid }) {
  return (
    <section className="empty-module">
      <div className="module-icon">◆</div>
      <h2>{title}</h2>
      <p>{description}</p>
      {actionLabel && (
        <button
          data-testid={testid || "empty-action"}
          className="secondary-button"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      )}
    </section>
  );
}
